import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessagesSquare, Plus } from 'lucide-react';
import { fetchProject, fetchThreads, type ThreadListStatus } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { projectNav, threadsHref } from '../lib/projectNav';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useCanonicalProjectRef } from '../hooks/useCanonicalProjectRef';
import { useCapabilities } from '../hooks/useCapabilities';
import { useFeature } from '../hooks/useEntitlements';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ProjectSectionNav } from '../components/ProjectSectionNav';
import { PageHeader } from '../components/PageHeader';
import { FeatureLocked } from '../components/FeatureLocked';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { ThreadListRow } from '../components/threads/ThreadListRow';

const FILTERS: Array<{ value: ThreadListStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
];

function parseStatus(raw: string | null): ThreadListStatus {
  return raw === 'resolved' ? 'resolved' : 'open';
}

/** The project's threads: long-running discussions with the team and the PM. */
export function ProjectThreadsPage() {
  const { projectId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = parseStatus(searchParams.get('status'));
  const { wp } = useWorkspacePath();
  const { can } = useCapabilities();
  const threadsOn = useFeature('chat');

  const projectQuery = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => fetchProject(projectId),
    enabled: !!projectId,
  });

  // Keep the URL on the pretty code; fetch threads by the resolved UUID.
  useCanonicalProjectRef(projectId, projectQuery.data?.code);
  const resolvedId = projectQuery.data?.id;

  const threadsQuery = useQuery({
    queryKey: qk.threadList(resolvedId ?? projectId, status),
    queryFn: () => fetchThreads(resolvedId!, status),
    enabled: !!resolvedId,
  });

  useDocumentTitle(projectQuery.data ? `Threads · ${projectQuery.data.name}` : 'Threads');

  if (projectQuery.isLoading) return <LoadingState label="Loading threads…" />;
  if (projectQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(projectQuery.error, 'Failed to load project')}
        onRetry={() => projectQuery.refetch()}
      />
    );
  }

  const project = projectQuery.data!;
  const nav = projectNav(project);
  const ref = project.code ?? project.id;
  const base = threadsHref(project.kind, ref);
  const canStart = can('thread:post', { projectId: project.id }) && threadsOn !== false;
  const threads = threadsQuery.data ?? [];

  return (
    <div className="space-y-6 pb-16 sm:pb-0">
      <Breadcrumbs
        items={[
          { label: nav.rootLabel, to: wp(nav.rootTo) },
          { label: nav.name, to: wp(nav.selfTo) },
          { label: 'Threads' },
        ]}
      />

      <PageHeader
        title="Threads"
        subtitle="Discussions with your team and the PM; the issues they surface are collected per thread."
        actions={
          canStart ? (
            <Link to={wp(`${base}/new`)} className="btn-primary">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New thread
            </Link>
          ) : undefined
        }
      />

      <ProjectSectionNav kind={project.kind} projectRef={ref} active="threads" />

      {threadsOn === false && <FeatureLocked feature="chat" />}

      <div className="flex items-center gap-1.5" role="group" aria-label="Filter threads">
        {FILTERS.map((f) => {
          const active = f.value === status;
          return (
            <button
              key={f.value}
              type="button"
              aria-pressed={active}
              className={`rounded-full border px-3 py-1 font-display text-2xs font-bold uppercase tracking-wide transition-colors ${
                active ? 'border-ink bg-ink text-surface-1' : 'border-line text-ink-muted hover:text-ink'
              }`}
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                if (f.value === 'open') next.delete('status');
                else next.set('status', f.value);
                setSearchParams(next, { replace: true });
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {threadsQuery.isLoading && <LoadingState label="Loading threads…" />}

      {threadsQuery.isError && (
        <ErrorState
          message={errorMessage(threadsQuery.error, 'Failed to load threads')}
          onRetry={() => threadsQuery.refetch()}
        />
      )}

      {threadsQuery.isSuccess && threads.length === 0 && (
        <EmptyState
          icon={<MessagesSquare className="h-6 w-6" aria-hidden="true" />}
          title={status === 'resolved' ? 'No resolved threads.' : 'No threads yet.'}
          description={
            status === 'resolved'
              ? 'Threads you resolve land here; a new post reopens them.'
              : 'Start one to think out loud with the PM — it keeps track of what you decide and which issues come out of it.'
          }
          action={
            canStart && status === 'open' ? (
              <Link to={wp(`${base}/new`)} className="btn-primary">
                <Plus className="h-4 w-4" aria-hidden="true" />
                New thread
              </Link>
            ) : undefined
          }
        />
      )}

      {threads.length > 0 && (
        <ul className="card divide-y divide-line overflow-hidden">
          {threads.map((t) => (
            <ThreadListRow key={t.id} thread={t} to={wp(`${base}/${t.id}`)} />
          ))}
        </ul>
      )}
    </div>
  );
}
